import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@repo/ui";
import { Eye, PencilSimple, Trash, Pill, Bell } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { PermissionGate } from "@/components/security";
import { deletePatient } from "@/lib/api/patients";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import type { Patient } from "@/lib/types/patients";

interface PatientTableProps {
  patients: Patient[];
  onView: (patient: Patient) => void;
  onEdit: (patient: Patient) => void;
  onDeleted: () => void;
}

function formatFullName(patient: Patient): string {
  const parts = [patient.first_name, patient.middle_name, patient.last_name];
  return parts.filter(Boolean).join(" ");
}

function formatAddress(patient: Patient): string {
  const parts = [patient.city, patient.state];
  const filtered = parts.filter(Boolean);
  return filtered.length > 0 ? filtered.join(", ") : "";
}

export function PatientTable({
  patients,
  onView,
  onEdit,
  onDeleted,
}: PatientTableProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [patientToDelete, setPatientToDelete] = useState<Patient | null>(null);

  const handleDeleteClick = (e: React.MouseEvent, patient: Patient) => {
    e.stopPropagation();
    setPatientToDelete(patient);
  };

  const handleConfirmDelete = async () => {
    if (!patientToDelete) return;

    const patient = patientToDelete;
    setPatientToDelete(null);
    setDeletingId(patient.id);
    try {
      await deletePatient(patient.id);
      toast({
        title: "Patient Deleted",
        description: `${formatFullName(patient)} has been removed.`,
        variant: "success",
      });
      onDeleted();
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async (token) => {
            await deletePatient(patient.id, { approvalToken: token });
            toast({
              title: "Patient Deleted",
              description: `${formatFullName(patient)} has been removed.`,
              variant: "success",
            });
            onDeleted();
          },
          actionDescription: "Delete patient",
        })
      )
        return;
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to delete patient",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (patients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
        <p className="text-sm font-medium text-foreground">No patients found</p>
        <p className="text-xs text-muted-foreground">
          Add a patient or adjust your search
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border bg-surface" data-testid="patient-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>DOB</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-center">Dispenses</TableHead>
              <TableHead className="text-center">Reminders</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {patients.map((patient) => {
              return (
                <TableRow
                  key={patient.id}
                  className="group cursor-pointer hover:bg-muted/50"
                  onClick={() => onView(patient)}
                  data-testid={`patient-row-${patient.id}`}
                >
                  <TableCell className="font-medium">
                    {formatFullName(patient)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {patient.date_of_birth
                      ? format(parseISO(patient.date_of_birth), "MM/dd/yyyy")
                      : "--"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {patient.phone_number ?? "--"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatAddress(patient) || "--"}
                  </TableCell>
                  <TableCell className="text-center">
                    <span
                      className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-medium tabular-nums ${
                        patient.dispense_count > 0
                          ? "bg-primary-soft text-primary"
                          : "text-muted-foreground"
                      }`}
                    >
                      {patient.dispense_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span
                      className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-medium tabular-nums ${
                        patient.reminder_count > 0
                          ? "bg-secondary-soft text-secondary"
                          : "text-muted-foreground"
                      }`}
                    >
                      {patient.reminder_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(patient);
                        }}
                        aria-label="View patient details"
                      >
                        <Eye size={15} />
                      </button>
                      <PermissionGate permission="patients:edit">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(patient);
                          }}
                          aria-label="Edit patient"
                        >
                          <PencilSimple size={15} />
                        </button>
                      </PermissionGate>
                      <PermissionGate permission="patients:delete">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                          onClick={(e) => handleDeleteClick(e, patient)}
                          disabled={deletingId === patient.id}
                          aria-label="Delete patient"
                        >
                          {deletingId === patient.id ? (
                            <Loader2 className="h-[15px] w-[15px] animate-spin" />
                          ) : (
                            <Trash size={15} />
                          )}
                        </button>
                      </PermissionGate>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <DeletePatientDialog
        patient={patientToDelete}
        onOpenChange={(open) => {
          if (!open) setPatientToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

// ── Delete confirmation dialog ─────────────────────────────────────────────

function DeletePatientDialog({
  patient,
  onOpenChange,
  onConfirm,
}: {
  patient: Patient | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  if (!patient) return null;

  const hasLinkedRecords = patient.dispense_count + patient.reminder_count > 0;
  const fullName = [patient.first_name, patient.middle_name, patient.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <AlertDialog open={!!patient} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Patient</AlertDialogTitle>
          <AlertDialogDescription>
            {hasLinkedRecords ? (
              <>
                <span className="font-semibold text-foreground">
                  &quot;{fullName}&quot;
                </span>{" "}
                cannot be deleted because they have linked records:
              </>
            ) : (
              <>
                Are you sure you want to delete{" "}
                <span className="font-semibold text-foreground">
                  &quot;{fullName}&quot;
                </span>
                ? This action cannot be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {hasLinkedRecords && (
          <div className="flex items-center gap-4 rounded-lg border bg-muted/40 px-4 py-3">
            {patient.dispense_count > 0 && (
              <div className="flex items-center gap-1.5">
                <Pill size={14} className="text-primary" />
                <span className="text-sm font-medium tabular-nums">
                  {patient.dispense_count}
                </span>
                <span className="text-sm text-muted-foreground">
                  dispense{patient.dispense_count !== 1 ? "s" : ""}
                </span>
              </div>
            )}
            {patient.reminder_count > 0 && (
              <div className="flex items-center gap-1.5">
                <Bell size={14} className="text-secondary" />
                <span className="text-sm font-medium tabular-nums">
                  {patient.reminder_count}
                </span>
                <span className="text-sm text-muted-foreground">
                  reminder{patient.reminder_count !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {hasLinkedRecords ? (
            <AlertDialogAction
              disabled
              className="bg-destructive text-white opacity-50 cursor-not-allowed hover:bg-destructive"
            >
              Delete
            </AlertDialogAction>
          ) : (
            <AlertDialogAction
              onClick={onConfirm}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
